import { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Field, Formik, FormikErrors, FormikValues } from 'formik'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import { TRAVEL_INTERESTS } from 'travelInterests'

import * as S from './style'

const ACCEPTED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'avif',
  'heic',
  'heif'
]

const ACCEPTED_IMAGE_INPUT =
  '.jpg,.jpeg,.png,.webp,.avif,.heic,.heif,image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif'

const STEP_TWO_FIELDS = [
  'firstname',
  'lastname',
  'password',
  'confirmPassword',
  'birthdate',
  'gender',
  'email'
]

const validate = (values: FormikValues) => {
  const errors: FormikErrors<FormikValues> = {}

  if (!values.email) {
    errors.email = 'Campo obrigatório'
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = 'E-mail inválido'
  }

  if (!values.password) errors.password = 'Insira sua senha'
  if (!values.firstname) errors.firstname = 'Este campo é obrigatório'
  if (!values.lastname) errors.lastname = 'Este campo é obrigatório'
  if (!values.birthdate) {
    errors.birthdate = 'Este campo é obrigatório'
  } else {
    const value = String(values.birthdate).slice(0, 10)
    const today = new Date()
    const cutoff = [
      String(today.getFullYear() - 18).padStart(4, '0'),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0')
    ].join('-')

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value > cutoff) {
      errors.birthdate = 'É necessário ter 18 anos ou mais para criar uma conta'
    }
  }

  if (values.password && values.password.length < 6) {
    errors.password = 'Insira no mínimo 6 caracteres'
  }

  if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'Senhas não conferem'
  }

  if (!values.gender || values.gender === '') {
    errors.gender = 'Campo obrigatório'
  }

  if (!values.profilePicture) {
    errors.profilePicture = 'Adicione uma foto de perfil'
  }

  if (!values.coverPicture) {
    errors.coverPicture = 'Adicione uma foto de capa'
  }

  if (!values.confirmPolicy) {
    errors.confirmPolicy = 'Você precisa aceitar a Política de Privacidade'
  }

  return errors
}

type RegisterFormProps = {
  step: number
  setStep: (step: number) => void
  selectedImage: string | null
  setSelectedImage: (value: string | null) => void
}

const RegisterForm = ({
  step,
  setStep,
  selectedImage,
  setSelectedImage
}: RegisterFormProps) => {
  const history = useHistory()
  const { signUp } = useAuth()
  const { uploadFile, updateUser, getUserData } = useUser()

  const [isLoading, setIsLoading] = useState(false)
  const [uploadingProfile, setUploadingProfile] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)

  const prepareLocalImage = (file?: File) => {
    if (!file) return null

    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!ACCEPTED_IMAGE_EXTENSIONS.includes(extension)) {
      toast.error('Use uma imagem JPG, JPEG, PNG, WebP, AVIF, HEIC ou HEIF.')
      return null
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 10 MB.')
      return null
    }

    return URL.createObjectURL(file)
  }

  const nextStep = useCallback(() => {
    setStep(step + 1)
  }, [setStep, step])

  return (
    <Formik
      initialValues={{
        firstname: '',
        lastname: '',
        country: '',
        birthdate: '',
        email: '',
        password: '',
        confirmPassword: '',
        interests: [],
        facebook: '',
        instagram: '',
        profilePicture: '',
        coverPicture: '',
        gender: 'M',
        about: '',
        confirmPolicy: false
      }}
      validate={validate}
      onSubmit={async (values) => {
        setIsLoading(true)

        try {
          const registrationValues = {
            ...values,
            profilePicture: '',
            coverPicture: ''
          }

          await signUp(registrationValues)

          try {
            if (!profileFile || !coverFile) {
              throw new Error('Fotos obrigatórias ausentes')
            }

            setUploadingProfile(true)
            setUploadingCover(true)

            const [profilePicture, coverPicture] = await Promise.all([
              uploadFile(profileFile, 'profile_picture'),
              uploadFile(coverFile, 'profile_cover')
            ])

            await updateUser({ profilePicture, coverPicture })
            await getUserData()

            toast.success('Conta criada com sucesso!', {
              position: 'top-right',
              autoClose: 1800,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: true,
              theme: 'dark'
            })

            window.location.replace('/minha-conta')
          } catch (uploadError) {
            toast.warning(
              'Sua conta foi criada, mas não foi possível concluir as fotos. Você pode adicioná-las em Editar perfil.',
              { theme: 'dark' }
            )
            window.location.replace('/editar-perfil')
          }
        } catch (error) {
          setIsLoading(false)
          toast.error(
            'Não foi possível criar sua conta. Verifique os dados e tente novamente.',
            {
              position: 'top-right',
              autoClose: 4000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: true,
              theme: 'dark'
            }
          )
        } finally {
          setUploadingProfile(false)
          setUploadingCover(false)
        }
      }}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        validateForm,
        setTouched,
        submitForm
      }) => (
        <form onSubmit={handleSubmit}>
          {step === 2 && (
            <S.FormContainer>
              <S.SecondFormTitle>
                <span>Preencha as</span> <span>informações</span>
              </S.SecondFormTitle>

              <S.InputWrapper>
                <S.Label>
                  Nome <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  as={S.Input}
                  name="firstname"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.firstname}
                />
                {errors.firstname && <S.Errors>{errors.firstname}</S.Errors>}
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label>
                  Sobrenome <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  as={S.Input}
                  name="lastname"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lastname}
                />
                {errors.lastname && <S.Errors>{errors.lastname}</S.Errors>}
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label>
                  Senha <S.Attention>*</S.Attention>
                </S.Label>
                <S.Info>Sua senha deve conter pelo menos 6 caracteres.</S.Info>
                <Field
                  as={S.Input}
                  type="password"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                />
                {errors.password && <S.Errors>{errors.password}</S.Errors>}
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label>
                  Repetir senha <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  as={S.Input}
                  type="password"
                  name="confirmPassword"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmPassword}
                />
                {errors.confirmPassword && (
                  <S.Errors>{errors.confirmPassword}</S.Errors>
                )}
              </S.InputWrapper>

              <S.InputDateWrapper>
                <S.Label>
                  Data de nascimento <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  as={S.InputDate}
                  name="birthdate"
                  type="date"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.birthdate}
                />
                <S.Info>
                  É necessário ter 18 anos ou mais para criar uma conta.
                </S.Info>
                {errors.birthdate && <S.Errors>{errors.birthdate}</S.Errors>}
              </S.InputDateWrapper>

              <S.InputDateWrapper>
                <S.Label>
                  Gênero <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  value={values.gender}
                  name="gender"
                  as={S.Select}
                  onChange={handleChange}
                >
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="NB">Não binário</option>
                  <option value="AN">Outro</option>
                </Field>
                {errors.gender && <S.Errors>{errors.gender}</S.Errors>}
              </S.InputDateWrapper>

              <S.InputWrapper>
                <S.Label>
                  E-mail <S.Attention>*</S.Attention>
                </S.Label>
                <Field
                  as={S.Input}
                  name="email"
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                />
                {errors.email && <S.Errors>{errors.email}</S.Errors>}
              </S.InputWrapper>

              <S.InputWrapper>
                <S.Label>Sobre você</S.Label>
                <Field
                  name="about"
                  as={S.TextArea}
                  onBlur={handleBlur}
                  value={values.about}
                  onChange={handleChange}
                  placeholder="Qual seu perfil de viajante? Qual foi sua melhor viagem? Do que você mais gosta?"
                />
              </S.InputWrapper>

              <S.Label>Selecione as suas opções de interesse:</S.Label>
              <S.GridContainer>
                {TRAVEL_INTERESTS.map((interest) => (
                  <S.GridItem key={interest.value}>
                    <Field
                      id={`register-interest-${interest.value}`}
                      name="interests"
                      value={interest.value}
                      type="checkbox"
                    />
                    <S.GridLabel
                      htmlFor={`register-interest-${interest.value}`}
                    >
                      {interest.label}
                    </S.GridLabel>
                  </S.GridItem>
                ))}
              </S.GridContainer>

              <S.SocialWrapper>
                <S.InputWrapper>
                  <S.Label>Instagram</S.Label>
                  <Field
                    as={S.InputDate}
                    type="text"
                    name="instagram"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.instagram}
                    placeholder="redsterna_ig"
                  />
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.Label>Facebook</S.Label>
                  <Field
                    as={S.InputDate}
                    type="text"
                    name="facebook"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.facebook}
                    placeholder="redsterna_fb"
                  />
                </S.InputWrapper>
              </S.SocialWrapper>

              <S.NextButton
                type="button"
                onClick={async () => {
                  const validationErrors: any = await validateForm()
                  const previousErrors = STEP_TWO_FIELDS.filter(
                    (field) => validationErrors[field]
                  )

                  if (previousErrors.length > 0) {
                    const touched = previousErrors.reduce(
                      (acc, field) => ({ ...acc, [field]: true }),
                      {}
                    )
                    await setTouched(touched)
                    toast.error(
                      'Preencha os campos obrigatórios antes de continuar.'
                    )
                    return
                  }

                  nextStep()
                }}
              >
                Continuar
              </S.NextButton>
            </S.FormContainer>
          )}

          {step === 3 && (
            <S.FormContainer>
              <S.ThirdFormTitle>
                <span>Só falta mais um pouco para finalizar seu</span>{' '}
                <span>cadastro!</span>
              </S.ThirdFormTitle>

              <S.PhotoGrid>
                <S.PhotoBlock>
                  <S.PhotoTitle>
                    Foto de perfil <S.Attention>*</S.Attention>
                  </S.PhotoTitle>
                  <S.PhotoDescription>
                    Escolha uma foto em que seu rosto apareça com clareza. A
                    imagem será recortada automaticamente em formato circular.
                  </S.PhotoDescription>

                  <input
                    type="file"
                    id="uploadProfile"
                    accept={ACCEPTED_IMAGE_INPUT}
                    style={{ display: 'none' }}
                    onChange={(event: any) => {
                      const file = event.target.files?.[0]
                      const preview = prepareLocalImage(file)

                      if (file && preview) {
                        setProfileFile(file)
                        setFieldValue('profilePicture', preview)
                        setSelectedImage(preview)
                      }
                    }}
                  />

                  <S.ProfileUploadLabel htmlFor="uploadProfile">
                    <S.ImageCircle>
                      {selectedImage || values.profilePicture ? (
                        <S.ProfilePreview
                          src={selectedImage || values.profilePicture}
                          alt="Foto de perfil"
                        />
                      ) : (
                        <S.ProfileUploadLogo src="/images/profile-upload-logo.svg" />
                      )}
                    </S.ImageCircle>
                    <S.UploadHint>
                      {uploadingProfile ? 'Enviando...' : 'Escolher foto'}
                    </S.UploadHint>
                  </S.ProfileUploadLabel>

                  {values.profilePicture && (
                    <S.RemovePhotoButton
                      type="button"
                      onClick={() => {
                        setFieldValue('profilePicture', '')
                        setProfileFile(null)
                        setSelectedImage(null)
                      }}
                    >
                      Remover foto
                    </S.RemovePhotoButton>
                  )}
                  {errors.profilePicture && (
                    <S.Errors>{errors.profilePicture}</S.Errors>
                  )}
                </S.PhotoBlock>

                <S.PhotoBlock $wide>
                  <S.PhotoTitle>
                    Foto de capa <S.Attention>*</S.Attention>
                  </S.PhotoTitle>
                  <S.PhotoDescription>
                    Prefira uma imagem horizontal. Fotos verticais também são
                    aceitas e serão recortadas automaticamente para preencher a
                    capa.
                  </S.PhotoDescription>

                  <input
                    type="file"
                    id="uploadCover"
                    accept={ACCEPTED_IMAGE_INPUT}
                    style={{ display: 'none' }}
                    onChange={(event: any) => {
                      const file = event.target.files?.[0]
                      const preview = prepareLocalImage(file)

                      if (file && preview) {
                        setCoverFile(file)
                        setFieldValue('coverPicture', preview)
                      }
                    }}
                  />

                  <S.LabelUpload htmlFor="uploadCover">
                    <S.CoverDiv $hasImage={Boolean(values.coverPicture)}>
                      {values.coverPicture ? (
                        <S.CoverPreview
                          src={values.coverPicture}
                          alt="Foto de capa"
                        />
                      ) : (
                        <S.ProfileUploadLogo src="/images/cover-upload-logo.svg" />
                      )}
                    </S.CoverDiv>
                    <S.UploadHint>
                      {uploadingCover ? 'Enviando...' : 'Escolher foto de capa'}
                    </S.UploadHint>
                  </S.LabelUpload>

                  {values.coverPicture && (
                    <S.RemovePhotoButton
                      type="button"
                      onClick={() => {
                        setFieldValue('coverPicture', '')
                        setCoverFile(null)
                      }}
                    >
                      Remover foto
                    </S.RemovePhotoButton>
                  )}
                  {errors.coverPicture && (
                    <S.Errors>{errors.coverPicture}</S.Errors>
                  )}
                </S.PhotoBlock>
              </S.PhotoGrid>

              <S.AgreeWrapper>
                <S.Checkbox
                  type="checkbox"
                  checked={Boolean(values.confirmPolicy)}
                  onChange={(event) =>
                    setFieldValue('confirmPolicy', event.target.checked)
                  }
                  onBlur={handleBlur}
                  name="confirmPolicy"
                />
                <S.PolicyText>
                  Ao cadastrar-se, você concorda com a{' '}
                  <S.Anchor href="/politicas-privacidade" target="_blank">
                    Política de Privacidade e os Termos de Uso
                  </S.Anchor>
                  .
                </S.PolicyText>
              </S.AgreeWrapper>
              {errors.confirmPolicy && (
                <S.PolicyError>{errors.confirmPolicy}</S.PolicyError>
              )}

              <S.Button
                type="button"
                disabled={isLoading || uploadingProfile || uploadingCover}
                onClick={async () => {
                  const validationErrors: any = await validateForm()
                  const errorKeys = Object.keys(validationErrors)

                  if (errorKeys.length > 0) {
                    const touched = errorKeys.reduce(
                      (acc, field) => ({ ...acc, [field]: true }),
                      {}
                    )
                    await setTouched(touched)

                    const previousStepError = STEP_TWO_FIELDS.some(
                      (field) => validationErrors[field]
                    )

                    if (previousStepError) {
                      toast.error(
                        'Há informações obrigatórias pendentes na etapa anterior. Vamos voltar para você corrigir.'
                      )
                      setStep(2)
                      return
                    }

                    if (
                      validationErrors.profilePicture ||
                      validationErrors.coverPicture
                    ) {
                      toast.error(
                        'Adicione as fotos obrigatórias para finalizar o cadastro.'
                      )
                      return
                    }

                    if (validationErrors.confirmPolicy) {
                      toast.error(
                        'Aceite a Política de Privacidade para finalizar o cadastro.'
                      )
                      return
                    }

                    toast.error(
                      'Revise os campos obrigatórios antes de finalizar.'
                    )
                    return
                  }

                  await submitForm()
                }}
              >
                {isLoading ? 'Criando conta...' : 'Finalizar cadastro'}
              </S.Button>
            </S.FormContainer>
          )}
        </form>
      )}
    </Formik>
  )
}

export default RegisterForm
